/**
 * Search, filters, language switch and PDF preview for the download library section.
 *
 * Every document row is rendered server side from the `download` metaobjects; this
 * element only shows and hides rows and swaps links. Without JavaScript all rows
 * stay visible with working download links, and the controls are hidden by CSS
 * (`:not(:defined)`).
 *
 * State lives in the query string (`q`, `product`, `type`, `lang`), so a menu entry
 * or a support mail can link straight to a filtered list.
 */
class DownloadLibrary extends HTMLElement {
  /** @type {Map<string, Promise<number | null>>} */
  static #sizes = new Map();

  static #shortQuery = window.matchMedia('(max-width: 599px)');

  /** @type {AbortController | null} */
  #controller = null;

  #state = { query: '', product: 'all', type: 'all', lang: 'de' };

  /** @type {HTMLElement[]} */
  #rows = [];

  /** @type {HTMLElement | null} */
  #opener = null;

  #announceTimer = 0;
  #urlTimer = 0;
  #toastTimer = 0;

  connectedCallback() {
    // Fresh controller per connect: the previous one was aborted on disconnect, and
    // addEventListener with an aborted signal silently attaches nothing.
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#rows = Array.from(this.querySelectorAll('[data-row]'));
    for (const row of this.#rows) {
      row.dataset.haystack = (row.dataset.search ?? '').toLocaleLowerCase();
    }

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('input', this.#handleInput, { signal });
    this.addEventListener('submit', (event) => event.preventDefault(), { signal });

    const dialog = this.#dialog;
    if (dialog) {
      dialog.addEventListener('close', this.#handleDialogClose, { signal });
      // A click on the dialog element itself can only be a click on the backdrop:
      // the content fills the whole box.
      dialog.addEventListener('click', (event) => event.target === dialog && dialog.close(), { signal });
    }

    if (this.#canPreview) {
      for (const hint of this.querySelectorAll('[data-new-tab-hint]')) hint.remove();
      for (const link of this.querySelectorAll('[data-preview]')) link.setAttribute('aria-haspopup', 'dialog');
    }

    DownloadLibrary.#shortQuery.addEventListener('change', this.#updatePlaceholder, { signal });
    this.#updatePlaceholder();

    this.#readUrl();
    this.#applyLanguage();
    this.#apply({ announce: false });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    window.clearTimeout(this.#announceTimer);
    window.clearTimeout(this.#urlTimer);
    window.clearTimeout(this.#toastTimer);
  }

  get #dialog() {
    const dialog = this.querySelector('dialog[data-dialog]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  get #input() {
    const input = this.querySelector('input[data-query]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** Embedding only helps where the browser renders PDFs inline — Android Chrome does not. */
  get #canPreview() {
    return Boolean(this.#dialog && typeof HTMLDialogElement.prototype.showModal === 'function' && navigator.pdfViewerEnabled);
  }

  /* ── State ──────────────────────────────────────────────── */

  #readUrl() {
    const params = new URLSearchParams(window.location.search);
    const hasChip = (/** @type {string} */ filter, /** @type {string | null} */ value) =>
      value !== null && this.querySelector(`[data-filter="${filter}"][data-value="${CSS.escape(value)}"]`) !== null;

    const product = params.get('product');
    const type = params.get('type');
    const lang = params.get('lang');

    this.#state.product = hasChip('product', product) && product ? product : 'all';
    this.#state.type = hasChip('type', type) && type ? type : 'all';
    this.#state.lang = lang === 'de' || lang === 'en' ? lang : this.dataset.defaultLang === 'en' ? 'en' : 'de';

    const input = this.#input;
    this.#state.query = input ? (params.get('q') ?? '').trim() : '';
    if (input) input.value = this.#state.query;
  }

  #writeUrl() {
    const url = new URL(window.location.href);
    const set = (/** @type {string} */ key, /** @type {string} */ value, /** @type {string} */ empty) => {
      if (value === empty) url.searchParams.delete(key);
      else url.searchParams.set(key, value);
    };
    set('q', this.#state.query, '');
    set('product', this.#state.product, 'all');
    set('type', this.#state.type, 'all');
    set('lang', this.#state.lang, this.dataset.defaultLang === 'en' ? 'en' : 'de');
    // Replace, not push: filtering is not a navigation step people expect the back
    // button to walk through.
    window.history.replaceState(window.history.state, '', url);
  }

  /* ── Events ─────────────────────────────────────────────── */

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const chip = target.closest('button[data-filter]');
    if (chip instanceof HTMLButtonElement) {
      const key = chip.dataset.filter === 'type' ? 'type' : 'product';
      this.#state[key] = chip.dataset.value ?? 'all';
      this.#apply({ announce: true });
      this.#writeUrl();
      return;
    }

    const langButton = target.closest('button[data-lang]');
    if (langButton instanceof HTMLButtonElement) {
      this.#state.lang = langButton.dataset.lang === 'en' ? 'en' : 'de';
      this.#applyLanguage();
      this.#writeUrl();
      return;
    }

    if (target.closest('[data-clear]')) {
      this.#state.query = '';
      const input = this.#input;
      if (input) {
        input.value = '';
        input.focus();
      }
      this.#apply({ announce: true });
      this.#writeUrl();
      return;
    }

    if (target.closest('[data-reset], [data-reset-all]')) {
      this.#state.query = '';
      this.#state.product = 'all';
      this.#state.type = 'all';
      const input = this.#input;
      if (input) input.value = '';
      this.#apply({ announce: true });
      this.#writeUrl();
      // The pressed button disappears with the reset; park focus on the search so
      // keyboard users are not dropped back at the top of the page.
      const fallback = input ?? this.querySelector('button[data-filter]');
      if (fallback instanceof HTMLElement) fallback.focus();
      return;
    }

    const preview = target.closest('a[data-preview]');
    if (preview instanceof HTMLAnchorElement && this.#canPreview) {
      const row = preview.closest('[data-row]');
      if (row instanceof HTMLElement) {
        event.preventDefault();
        this.#openPreview(row, preview);
      }
      return;
    }

    if (target.closest('[data-dialog-close]')) {
      this.#dialog?.close();
      return;
    }

    const download = target.closest('a[data-download]');
    if (download instanceof HTMLAnchorElement) this.#showToast(download);
  };

  /** @param {Event} event */
  #handleInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-query]')) return;

    this.#state.query = input.value.trim();
    this.#apply({ announce: true });

    window.clearTimeout(this.#urlTimer);
    this.#urlTimer = window.setTimeout(() => this.#writeUrl(), 400);
  };

  #updatePlaceholder = () => {
    const input = this.#input;
    if (!input) return;
    const short = DownloadLibrary.#shortQuery.matches;
    input.placeholder = (short ? input.dataset.placeholderShort : input.dataset.placeholder) ?? '';
  };

  /* ── Filtering ──────────────────────────────────────────── */

  /** @param {{announce: boolean}} options */
  #apply(options) {
    const { query, product, type } = this.#state;
    const words = query.toLocaleLowerCase().split(/\s+/).filter(Boolean);

    /** @type {Map<string, number>} */
    const productCounts = new Map([['all', 0]]);
    /** @type {Map<string, number>} */
    const typeCounts = new Map([['all', 0]]);
    let visible = 0;

    for (const row of this.#rows) {
      const haystack = row.dataset.haystack ?? '';
      const matchesQuery = words.every((word) => haystack.includes(word));
      const rowProduct = row.dataset.product ?? '';
      const rowType = row.dataset.type ?? '';
      const matchesProduct = product === 'all' || rowProduct === product;
      const matchesType = type === 'all' || rowType === type;

      // Each chip counts what you would see after pressing it: its own dimension
      // swapped, the other filter and the search kept.
      if (matchesQuery && matchesType) {
        productCounts.set('all', (productCounts.get('all') ?? 0) + 1);
        productCounts.set(rowProduct, (productCounts.get(rowProduct) ?? 0) + 1);
      }
      if (matchesQuery && matchesProduct) {
        typeCounts.set('all', (typeCounts.get('all') ?? 0) + 1);
        typeCounts.set(rowType, (typeCounts.get(rowType) ?? 0) + 1);
      }

      const show = matchesQuery && matchesProduct && matchesType;
      row.hidden = !show;
      if (show) visible++;
    }

    for (const chip of this.querySelectorAll('button[data-filter]')) {
      if (!(chip instanceof HTMLElement)) continue;
      const isType = chip.dataset.filter === 'type';
      const value = chip.dataset.value ?? 'all';
      const chipCount = (isType ? typeCounts : productCounts).get(value) ?? 0;
      chip.setAttribute('aria-pressed', String(value === (isType ? type : product)));
      const count = chip.querySelector('[data-count]');
      if (count) count.textContent = String(chipCount);
      // Screen readers hear "Portal, 2 Dokumente", not a bare number.
      const countLabel = chip.querySelector('[data-count-label]');
      if (countLabel) countLabel.textContent = ` ${this.#noun(chipCount)}`;
    }

    for (const group of this.querySelectorAll('[data-group]')) {
      if (!(group instanceof HTMLElement)) continue;
      const count = group.querySelectorAll('[data-row]:not([hidden])').length;
      group.hidden = count === 0;
      const label = group.querySelector('[data-group-count]');
      if (label) label.textContent = this.#countLabel(count);
    }

    const resultCount = this.querySelector('[data-result-count]');
    if (resultCount) resultCount.textContent = String(visible);
    const resultLabel = this.querySelector('[data-result-label]');
    if (resultLabel) resultLabel.textContent = this.#noun(visible);

    const groups = this.querySelector('[data-groups]');
    if (groups instanceof HTMLElement) groups.hidden = visible === 0;
    const empty = this.querySelector('[data-empty]');
    if (empty instanceof HTMLElement) empty.hidden = visible !== 0;

    const reset = this.querySelector('[data-reset]');
    if (reset instanceof HTMLElement) reset.hidden = !query && product === 'all' && type === 'all';
    const clear = this.querySelector('[data-clear]');
    if (clear instanceof HTMLElement) clear.hidden = !query;

    if (options.announce) this.#announce(visible);
  }

  /** @param {number} count */
  #noun(count) {
    return (count === 1 ? this.dataset.resultOne : this.dataset.resultMany) ?? '';
  }

  /** @param {number} count */
  #countLabel(count) {
    return `${count} ${this.#noun(count)}`.trim();
  }

  /**
   * Debounced, so typing "datenblatt" reads one result count instead of ten.
   * @param {number} count
   */
  #announce(count) {
    window.clearTimeout(this.#announceTimer);
    this.#announceTimer = window.setTimeout(() => {
      const status = this.querySelector('[data-status]');
      if (status) status.textContent = count === 0 ? (this.dataset.statusNone ?? '') : this.#countLabel(count);
    }, 700);
  }

  /* ── Language ───────────────────────────────────────────── */

  #applyLanguage() {
    const wanted = this.#state.lang;

    for (const button of this.querySelectorAll('button[data-lang]')) {
      if (button instanceof HTMLElement) button.setAttribute('aria-pressed', String(button.dataset.lang === wanted));
    }

    for (const row of this.#rows) {
      const lang = this.#rowLanguage(row, wanted);
      const url = (lang === 'en' ? row.dataset.urlEn : row.dataset.urlDe) ?? '';
      const file = (lang === 'en' ? row.dataset.fileEn : row.dataset.fileDe) ?? '';

      for (const link of row.querySelectorAll('a[data-preview], a[data-download]')) {
        if (!(link instanceof HTMLAnchorElement)) continue;
        link.href = url;
        link.hreflang = lang;
        if (link.hasAttribute('download')) link.download = file;
      }

      const name = (lang === 'en' ? this.dataset.languageEn : this.dataset.languageDe) ?? '';
      const langName = row.querySelector('[data-lang-name]');
      if (langName) langName.textContent = name;

      const fallback = row.querySelector('[data-fallback]');
      if (fallback instanceof HTMLElement) {
        fallback.hidden = lang === wanted;
        fallback.textContent = (this.dataset.fallbackText ?? '').replace('{language}', name);
      }

      this.#fillSize(row, url);
    }
  }

  /**
   * The wanted language if the row has that file, otherwise the one it has.
   * @param {HTMLElement} row
   * @param {string} wanted
   */
  #rowLanguage(row, wanted) {
    if (wanted === 'en') return row.dataset.urlEn ? 'en' : 'de';
    return row.dataset.urlDe ? 'de' : 'en';
  }

  /* ── File size ──────────────────────────────────────────── */

  /**
   * Shopify does not expose a file's size to Liquid, so it is asked from the CDN.
   * If that fails (offline, CORS), the meta line simply has no size.
   * @param {HTMLElement} row
   * @param {string} url
   */
  #fillSize(row, url) {
    const slot = row.querySelector('[data-size]');
    if (!slot || !url) return;

    slot.textContent = '';
    DownloadLibrary.#fileSize(url).then((bytes) => {
      // The language may have flipped while the request was in flight.
      const current = this.#rowLanguage(row, this.#state.lang) === 'en' ? row.dataset.urlEn : row.dataset.urlDe;
      if (bytes && current === url) slot.textContent = ` · ${DownloadLibrary.#formatSize(bytes)}`;
    });
  }

  /**
   * A one-byte range request instead of HEAD: Shopify's CDN answers HEAD without
   * Content-Length, but reports the full size in Content-Range ("bytes 0-0/1210").
   * @param {string} url
   */
  static #fileSize(url) {
    let request = DownloadLibrary.#sizes.get(url);
    if (!request) {
      const controller = new AbortController();
      request = fetch(url, { headers: { Range: 'bytes=0-0' }, signal: controller.signal })
        .then((response) => {
          // A 206 carries the one-byte slice, so its Content-Length is 1. Only
          // Content-Range knows the total; without it (unexposed cross-origin,
          // "bytes 0-0/*") show no size rather than "1 KB".
          if (response.status === 206) {
            const total = Number(response.headers.get('content-range')?.split('/').pop());
            return total > 0 ? total : null;
          }
          // The server ignored the range: take the length, but stop the body so a
          // page with many manuals does not quietly download all of them.
          const length = Number(response.headers.get('content-length'));
          controller.abort();
          return response.status === 200 && length > 0 ? length : null;
        })
        .catch(() => null);
      DownloadLibrary.#sizes.set(url, request);
    }
    return request;
  }

  /** @param {number} bytes */
  static #formatSize(bytes) {
    const locale = document.documentElement.lang || 'de';
    if (bytes >= 1024 * 1024) {
      const megabytes = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(bytes / (1024 * 1024));
      return `${megabytes} MB`;
    }
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  /* ── Preview ────────────────────────────────────────────── */

  /**
   * @param {HTMLElement} row
   * @param {HTMLElement} opener
   */
  #openPreview(row, opener) {
    const dialog = this.#dialog;
    if (!dialog) return;

    const lang = this.#rowLanguage(row, this.#state.lang);
    const url = (lang === 'en' ? row.dataset.urlEn : row.dataset.urlDe) ?? '';
    const file = (lang === 'en' ? row.dataset.fileEn : row.dataset.fileDe) ?? '';
    const title = row.querySelector('.dlib__name')?.textContent?.trim() ?? '';

    const kicker = dialog.querySelector('[data-dialog-kicker]');
    if (kicker) kicker.textContent = [row.dataset.typeLabel, row.dataset.productLabel].filter(Boolean).join(' · ');
    const heading = dialog.querySelector('[data-dialog-title]');
    if (heading) heading.textContent = title;

    const meta = dialog.querySelector('[data-dialog-meta]');
    const rowMeta = row.querySelector('[data-meta]');
    if (meta && rowMeta) meta.replaceChildren(...Array.from(rowMeta.cloneNode(true).childNodes));

    const download = dialog.querySelector('a[data-dialog-download]');
    if (download instanceof HTMLAnchorElement) {
      download.href = url;
      download.hreflang = lang;
      download.download = file;
    }
    const newTab = dialog.querySelector('a[data-dialog-new-tab]');
    if (newTab instanceof HTMLAnchorElement) {
      newTab.href = url;
      newTab.hreflang = lang;
    }

    const frame = dialog.querySelector('iframe[data-dialog-frame]');
    if (frame instanceof HTMLIFrameElement) {
      frame.title = `${this.dataset.previewFrameLabel ?? ''}: ${title}`;
      frame.src = url;
    }

    this.#opener = opener;
    dialog.showModal();
    const close = dialog.querySelector('[data-dialog-close]');
    if (close instanceof HTMLElement) close.focus();
  }

  #handleDialogClose = () => {
    const frame = this.#dialog?.querySelector('iframe[data-dialog-frame]');
    // Unload the PDF so a closed dialog does not keep a viewer running.
    if (frame instanceof HTMLIFrameElement) frame.src = 'about:blank';
    this.#opener?.focus();
    this.#opener = null;
  };

  /* ── Toast ──────────────────────────────────────────────── */

  /** @param {HTMLAnchorElement} link */
  #showToast(link) {
    if (!this.hasAttribute('data-toast-enabled')) return;

    // A modal dialog makes the rest of the page inert, so the toast inside the dialog
    // is the only one that can be seen and announced while it is open.
    const dialog = this.#dialog;
    const host = dialog?.open ? dialog : this;
    const toast = Array.from(host.querySelectorAll('[data-toast]')).find(
      (element) => element.closest('dialog') === (dialog?.open ? dialog : null)
    );
    const text = toast?.querySelector('[data-toast-text]');
    if (!(toast instanceof HTMLElement) || !text) return;

    const file = link.download || decodeURIComponent(link.pathname.split('/').pop() ?? '');
    text.textContent = (this.dataset.toastText ?? '').replace('{file}', file);
    toast.classList.add('is-visible');

    window.clearTimeout(this.#toastTimer);
    this.#toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2800);
  }
}

if (!customElements.get('download-library')) {
  customElements.define('download-library', DownloadLibrary);
}
